import * as wanakana from 'wanakana'

export interface KanaSuggestion {
  text: string
  type: 'hiragana' | 'katakana' | 'romaji'
  autoCorrectedFrom?: string
}

export const COMMON_ROMAJI_CORRECTIONS: Record<string, string> = {
  konichiwa: 'konnichiha',
  konnichiwa: 'konnichiha',
  konbanwa: 'konbanha',
}

export const applyAutoCorrectRules = (input: string): string[] => {
  const variants = new Set<string>()
  const lower = input.toLowerCase()

  if (COMMON_ROMAJI_CORRECTIONS[lower]) {
    variants.add(COMMON_ROMAJI_CORRECTIONS[lower])
  }

  if (lower.endsWith('wa') && lower.length > 2) {
    variants.add(input.slice(0, -2) + input.slice(-2).replace(/wa$/i, 'ha'))
  }

  if (/nichi/.test(lower) && !/nnichi/.test(lower)) {
    variants.add(input.replace(/nichi/gi, 'nnichi'))
  }

  return Array.from(variants)
}

export const generateRomajiVariants = (
  word: string
): Array<{ variant: string; autoCorrectedFrom?: string }> => {
  const variants: Array<{ variant: string; autoCorrectedFrom?: string }> = []
  const seen = new Set<string>()
  const queue: Array<{ variant: string; autoCorrectedFrom?: string }> = [{ variant: word }]

  while (queue.length) {
    const current = queue.shift()!
    const key = current.variant.toLowerCase()
    if (seen.has(key)) continue

    seen.add(key)
    variants.push(current)

    const autoCorrectedFrom = current.autoCorrectedFrom ?? word
    applyAutoCorrectRules(current.variant).forEach((variant) => {
      queue.push({
        variant,
        autoCorrectedFrom,
      })
    })
  }

  return variants
}

export const buildKanaSuggestions = (word: string): KanaSuggestion[] => {
  if (!word || word.trim() === '') return []

  const suggestions: KanaSuggestion[] = []
  const hasRomaji = /[a-zA-Z]/.test(word)

  const variantData = hasRomaji ? generateRomajiVariants(word) : []
  const seenTexts = new Set<string>()

  const addSuggestion = (text: string, type: KanaSuggestion['type'], autoCorrectedFrom?: string) => {
    const key = `${type}:${text}`
    if (seenTexts.has(key)) return
    seenTexts.add(key)
    suggestions.push({ text, type, autoCorrectedFrom })
  }

  if (hasRomaji) {
    variantData.forEach(({ variant, autoCorrectedFrom }) => {
      const hiragana = wanakana.toHiragana(variant)
      if (hiragana !== variant) {
        addSuggestion(hiragana, 'hiragana', autoCorrectedFrom !== variant ? autoCorrectedFrom : undefined)
      }

      const katakana = wanakana.toKatakana(variant)
      if (katakana !== variant && katakana !== hiragana) {
        addSuggestion(katakana, 'katakana', autoCorrectedFrom !== variant ? autoCorrectedFrom : undefined)
      }
    })

    addSuggestion(word, 'romaji')
  } else {
    // Already looks like Japanese text; treat as hiragana suggestion
    addSuggestion(word, 'hiragana')
  }

  return suggestions
}

export const normalizeWordForTranslation = (input: string): string => {
  const hasRomajiChars = /[a-zA-Z]/.test(input)
  let normalized = hasRomajiChars ? input : wanakana.toRomaji(input)
  const visited = new Set<string>()

  while (true) {
    const lowered = normalized.toLowerCase()
    if (visited.has(lowered)) {
      break
    }
    visited.add(lowered)
    const corrections = applyAutoCorrectRules(normalized)
    const next = corrections.find((variant) => !visited.has(variant.toLowerCase()))
    if (!next) {
      break
    }
    normalized = next
  }

  return wanakana.toHiragana(normalized)
}
